import type { ShopifyProduct, ShopifyProductVariant } from "../api/ShopifyApi";
import type { Wishlist } from "../api/WishlistApi";
import type {
  WishlistActionName,
  WishlistStateAction,
} from "../state/WishlistState";
import { createDebug } from "../utils/LazyDebug";

const debug = createDebug("wk:events");

export class WishlistEvents {
  dispatcher = document;

  publish(message: WishlistEvent) {
    const event = new CustomEvent(`wk:${message.name}`, {
      detail: message,
      bubbles: false,
      cancelable: false,
    });
    debug(`Publish: ${message.name}`, { message, event });
    this.dispatcher.dispatchEvent(event);
  }

  once(eventName: WishlistEventName, handler: (event: WishlistEvent) => void) {
    this.subscribe(eventName, handler, { once: true });
  }

  subscribe(
    eventName: WishlistEventName,
    handler: (event: WishlistEvent) => void,
    options?: { once?: boolean },
  ) {
    // For backwards compatibility
    if (eventName.startsWith("wk:")) {
      eventName = eventName.replace("wk:", "") as WishlistEventName;
    }
    debug(`Subscribe: ${eventName}`, { handler, options });
    this.dispatcher.addEventListener(
      `wk:${eventName}`,
      (event) => {
        const customEvent = event as CustomEvent;
        handler({
          name: customEvent.type.replace("wk:", "") as WishlistEventName,
          data: customEvent.detail.data,
        });
      },
      {
        once: !!options?.once,
      },
    );
  }

  subscribeMany(events: {
    [key in WishlistEventName]?: (event: WishlistEvent) => void;
  }) {
    return Object.entries(events).map(([eventName, handler]) =>
      this.subscribe(eventName as WishlistEventName, handler),
    );
  }
}

export interface WishlistEvent {
  name: WishlistEventName;
  data?: {
    quantity?: number;
    productHandle?: string;
    product?: ShopifyProduct | null;
    variantId?: number;
    variant?: ShopifyProductVariant;
    wishlist?: Wishlist;
    action?: WishlistActionName;
    sessionId?: string;
    customerEmail?: string;
    pending?: {
      action: WishlistActionName;
      productHandle?: string;
      variantId?: number;
    };
    // TODO: Refactor pending and action to use this state data
    stateAction?: WishlistStateAction;
  };
  error?: Error;
}

export type WishlistEventName =
  // | "wishlist:add:error"
  // | "wishlist:remove:error"
  // | "wishlist:change-variant:error"
  // | "wishlist:clear:error"
  // | "wishlist:share:error"
  // | "wishlist:add-to-cart:error"
  // | "wishlist:add-all-to-cart:error"
  | "app:ready"
  | "app:init-sid"
  | "product:change-variant:success"
  | "wishlist:add:success"
  | "wishlist:remove:success"
  | "wishlist:change-variant:success"
  | "wishlist:clear:success"
  | "wishlist:share:success"
  | "wishlist:add-to-cart:success"
  | "wishlist:add-all-to-cart:success"
  | "wishlist:save-for-later:success"
  | "wishlist:page-view"
  | "wishlist:login-required"
  | "state:action"
  | "state:refetch"
  | "customer:login"
  | "customer:logout";
